import clsx from "clsx";
import { shallowEqual } from "react-redux";
import React from "react";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import { CarConfigLabel, useCarConfigOptionalLabel } from "../../../../../utils/labelUtils";
import styles from "./SummaryTotalOverview.module.scss";
import { useCarConfigSelector } from "../../../../../redux/store";
import { financeCtaSelector, pricingDetailsSelector } from "../../../../../utils/selectorUtils";
import Price from "../../../common/price/Price";
import { getAemFlexibilityMatrixConfig } from "../../../../../../../../common-deprecated/settings/utils/financeSettingUtilsAem";
import { formatMonthlyFinancePrice } from "../../../../../../shared-logic/utils/priceUtils";
import { AemFmComponent } from "../../../../../../../../common-deprecated/utils/aemFlexibilityMatrixUtils";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import { MODAL_FINANCE_DISCLAIMER } from "../../../../../utils/modalConstants";
import useAphModal from "../../../../../hooks/useAphModal";
import { useTabsContext } from "./SummaryTabsContext";
import useAphSummaryTabs from "../../../../../hooks/useAphSummaryTabs";

const SummaryTotalOverview: React.FC = () => {
    const basketTotal = useCarConfigSelector((state) => state.basket.total);
    const financeOption = useCarConfigSelector((state) => state.utils.financeOption);
    const tabsContext = useTabsContext();
    const { onTabSwitch } = useAphSummaryTabs(tabsContext);
    const { total, commonSettings, info, modelCode, priceReadyToShow, showFinance } = useCarConfigSelector(
        financeCtaSelector,
        shallowEqual,
    );
    const { forceCashPrice, alwaysShowCashDisclaimer, showFinanceDisclaimerInline } = useCarConfigSelector(
        pricingDetailsSelector,
        shallowEqual,
    );

    const carConfigCashPriceDisclaimer = useCarConfigOptionalLabel("carConfigCashPriceDisclaimer");
    const totalLabel = financeOption === "cash" || forceCashPrice ? "carConfigCashTotal" : "carConfigMontlyTotal";

    const { showModalHelper } = useAphModal();

    const priceFormat =
        getAemFlexibilityMatrixConfig(commonSettings, AemFmComponent.Carconfig, "new", modelCode)?.config?.finance
            ?.format || "";

    const monthlyRateItems = formatMonthlyFinancePrice<string>(
        { ...basketTotal, monthly: Number(total) },
        info,
        commonSettings,
        priceFormat,
        false,
        basketTotal.monthly,
        true,
    );

    const getPriceDisclaimer = (): string => {
        if (financeOption === "cash") return carConfigCashPriceDisclaimer ?? "";
        else return info.disclaimer?.value ?? "";
    };

    return (
        <div className={clsx(styles.cta, styles.inpage)}>
            {/* Price and it's corresponding disclaimer */}
            <div className={styles["inpage-price"]}>
                <Text variant="apheleia-h4">
                    <CarConfigLabel label={totalLabel} />
                </Text>
                <Text variant="apheleia-h4">
                    {financeOption === "monthly" && showFinanceDisclaimerInline && !forceCashPrice ? (
                        <span dangerouslySetInnerHTML={{ __html: monthlyRateItems }} />
                    ) : (
                        <strong>
                            <Price
                                price={basketTotal}
                                forceCashPrice={forceCashPrice}
                                financeItemId="total"
                                readyToShow={priceReadyToShow}
                            />
                        </strong>
                    )}
                </Text>
                {(forceCashPrice || alwaysShowCashDisclaimer || showFinanceDisclaimerInline) && (
                    <div className={styles["inpage-price-disclaimer"]}>
                        {/* forceCashPrice: show cash price with it's disclaimer. Else show disclaimer accordingly. */}
                        {forceCashPrice ? (
                            <>
                                <span
                                    className={styles.disclaimer}
                                    dangerouslySetInnerHTML={{ __html: monthlyRateItems }}
                                />
                                <Button
                                    variant="apheleia-text"
                                    onClick={() => showModalHelper(MODAL_FINANCE_DISCLAIMER, {})}
                                >
                                    <CarConfigLabel label="carConfigReadCoreFinanceTerms" />
                                </Button>
                            </>
                        ) : (
                            <span
                                className={`${styles.disclaimer} ${styles["disclaimer-start"]}`}
                                dangerouslySetInnerHTML={{ __html: getPriceDisclaimer() }}
                            />
                        )}
                    </div>
                )}
                {!["insurance", "finance"].includes(tabsContext.openTab || "") && showFinance && (
                    <div className={styles["inpage-price-disclaimer"]}>
                        <Button variant="apheleia-text" onClick={() => onTabSwitch("finance")}>
                            <CarConfigLabel label="carConfigFinanceOptions" />
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SummaryTotalOverview;
